import React, { useEffect, useRef, useState } from "react";
import { BuyOptionCard } from "web/react/components/buy-option-card/buy-option-card";
import { PricingCardPDP } from "web/react/components/pdp-pricing-card";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import { Price } from "web/react/emo/price";
import { Sticker } from "web/react/emo/sticker";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import { BuyButton } from "web/react/pages/product/in-stock/in-stock-product-page/buy-button";
import {
    ExpressCheckoutBuyDirectButton,
    ExpressCheckoutDisclaimer,
    ExpressCheckoutOrSeparator,
} from "web/react/pages/product/in-stock/in-stock-product-page/express-checkout-disclaimer/express-checkout-disclaimer";
import {
    ProductPurchaseType,
    ProductPurchaseTypes,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import ProductHeader from "web/react/pages/product/in-stock/in-stock-product-page/product-area/product-info/product-header/product-header";
import { ComparePriceLink } from "web/react/pages/product/in-stock/in-stock-product-page/product-price-comparison/product-price-comparison";
import { SizeArea } from "web/react/pages/product/in-stock/in-stock-product-page/size-area";
import { getBuyOptionStickerType } from "web/react/pages/product/in-stock/in-stock-product-page/utils";
import { ProductStatus } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import { canUseMembership } from "web/script/modules/utils";
import { ProductBuyOptionsSerializer } from "web/types/serializers";
import * as styles from "./product-info.css";

export function handleSizeExpanded(
    productPurchaseType: ProductPurchaseTypes,
    isExpanded: boolean,
    isComparison: boolean
): void {
    const isDeepAffiliatePhase1Enabled = environment.getFeature(
        "deep_affiliate_experiment_phase_1"
    );
    const isDeepAffiliateOrCheckout =
        isDeepAffiliatePhase1Enabled || productPurchaseType === ProductPurchaseType.CHECKOUT;

    if (isDeepAffiliateOrCheckout) {
        analytics.event("size_dropdown", "clicked", "component");
    } else {
        const action = isExpanded ? "close_size_picker" : "open_size_picker";
        analytics.event(
            isComparison ? "compare_prices_size_picker" : "size_picker",
            action,
            undefined,
            false,
            null,
            `${productPurchaseType}.size_picker.${action}`,
            null,
            undefined,
            true
        );
    }
}

/**
 * @typedef ProductInfoProps
 * @description type to infer buy option if modal is present
 */
type ProductInfoProps =
    | {
          isModal: boolean;
          buyOption: ProductBuyOptionsSerializer;
      }
    | {
          isModal?: never;
          buyOption?: never;
      };

export function ProductInfo({
    isModal,
    buyOption: modalBuyOption,
}: ProductInfoProps): React.ReactElement {
    const { isMobileViewport, isTabletViewport, isDesktopViewport } = useDomViewport();
    const {
        designer,
        product,
        sizePicker: { sizes: contextSizes },
        activeBuyOption: { buyOption: activeBuyOption, promotion, index: activeBuyOptionIndex },
        activeProduct: { cardSizeList, shippingInformation, price, selectedSizeOption },
        buyOptions,
        isLoggedIn,
        productPurchaseType,
        isChatGptTraffic,
        comparison: { openCompareModal, displayedBuyOptions },
        comparePricesRef,
    } = useInStockProductPageContext();
    // set buy option depending on if the product info is on a regular PDP or in the checkout modal with the isModal flag
    const buyOption = isModal ? modalBuyOption : activeBuyOption;
    const buyOptionPrice = isModal
        ? {
              fullPrice: modalBuyOption.price,
              inStock: Boolean(modalBuyOption.in_stock),
              salePrice: modalBuyOption.sale_price,
          }
        : price;
    const buyOnStoreBtnRef = useRef<HTMLImageElement | null>(null);

    const [isSizePickerExpanded, setIsSizePickerExpanded] = useState(false);

    const isExpressCheckout = productPurchaseType === ProductPurchaseType.EXPRESS_CHECKOUT;
    const isChatGptExpressCheckout = isExpressCheckout && isChatGptTraffic;

    // Show multiple stickers on PDPs with BuyOptionCard components, i.e. Deep Affiliate or Checkout
    const stickers = buyOption.labels
        .map((label) => getBuyOptionStickerType(label))
        .filter((sticker) => sticker !== undefined);

    // Show one sticker on normal affiliate PDPs
    const stickerType = stickers[0];

    const handleSizeLinkClick = (expanded: boolean, shouldSendAnalytic = true): void => {
        setIsSizePickerExpanded((isSizePickerExpanded) => !isSizePickerExpanded);

        if (isExpressCheckout) {
            shouldSendAnalytic && analytics.event("express_checkout", "select_box_clicked");
        } else if (expanded) {
            handleSizeExpanded(productPurchaseType, isSizePickerExpanded, false);
        }
    };

    const isDeepAffiliatePhase1Enabled = environment.getFeature(
        "deep_affiliate_experiment_phase_1"
    );
    const isCheckout = productPurchaseType === ProductPurchaseType.CHECKOUT;

    const isDeepAffiliateOrCheckout = isDeepAffiliatePhase1Enabled || isCheckout;

    const buyOptionCardRef = useRef<HTMLDivElement>(null);
    const [isBuyOptionCardVisible] = useIntersectionObserver(
        {
            threshold: 0,
            rootMargin: "0px",
            once: true,
        },
        buyOptionCardRef
    );

    useEffect(() => {
        if (isBuyOptionCardVisible) {
            analytics.event(
                "main_buy_option_card",
                "impression",
                "",
                true,
                {},
                "",
                {},
                {
                    product_id: product.product_id,
                    buy_option_id: buyOption.link_id,
                    retailer_network: buyOption.retailer_network,
                    retailer_slug: buyOption.retailer_slug,
                    buy_option_is_checkout: buyOption.is_icon,
                    product_has_checkout: isCheckout,
                },
                false,
                false
            );
        }
    }, [isBuyOptionCardVisible, buyOption, isCheckout]);

    function onClickCompare(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void {
        event.preventDefault();

        if (isTabletViewport || isDesktopViewport) {
            comparePricesRef?.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
            });
        } else {
            openCompareModal();
        }

        const label = isCheckout
            ? "checkout"
            : isDeepAffiliatePhase1Enabled
            ? "deep_affiliate"
            : "pdp";

        analytics.event("compare-prices", "clicked", label);
    }

    function onShowCompare(): void {
        const label = isCheckout
            ? "checkout"
            : isDeepAffiliatePhase1Enabled
            ? "deep_affiliate"
            : "pdp";

        analytics.event("compare-prices", "shown", label);
    }

    function onRetailerClick(): void {
        analytics.event("retailer_store", "clicked", "product_details", false, {
            retailerNetwork: buyOption.retailer_network,
        });
    }

    const sizes = !!isModal ? buyOption.sizes : contextSizes;
    const buyOptionStatus: ProductStatus = !buyOption.in_stock
        ? "out_of_stock"
        : buyOption.on_sale
        ? "on_sale"
        : "in_stock";

    if (isDeepAffiliateOrCheckout) {
        const cardProps = {
            variant: "narrow" as const,
            stickers: stickers,
            buyOption: activeBuyOption,
            parentArea: "product_details" as const,
            retailerLogoURL: activeBuyOption.retailer_image || undefined,
            shippingInformation: shippingInformation,
            comparePricesCount: displayedBuyOptions.length,
            onClickCompare: onClickCompare,
            onShowCompare: onShowCompare,
            onRetailerClick: onRetailerClick,
            promotion: promotion,
        };

        return (
            <VStack spacing="sm">
                <HStack justify="space-between" dataTestId="product-header">
                    <VStack spacing="none">
                        <Heading textStyle={"subhead"} dataTestId="product-name">
                            {designer.name}
                        </Heading>
                        <Text textStyle={"body-s"} dataTestId="product-short-description">
                            {product.short_description}
                        </Text>
                    </VStack>
                    <SaveForLaterButton
                        isLoggedIn={isLoggedIn}
                        type="icon"
                        variant="emotional"
                        productId={product.product_id}
                        productStatus={buyOptionStatus}
                        pageSource="pdp"
                        linkId={activeBuyOption.link_id}
                    />
                </HStack>
                {sizes && (
                    <SizeArea
                        sizes={sizes}
                        isSizePickerExpanded={isSizePickerExpanded}
                        handleSizeLinkClick={handleSizeLinkClick}
                        isModal={!!isModal}
                    />
                )}
                <div data-testid="buy-option-card" ref={buyOptionCardRef}>
                    {isCheckout ? (
                        <BuyOptionCard
                            {...cardProps}
                            isCheckout
                            buyOptionIndex={activeBuyOptionIndex}
                        />
                    ) : (
                        <BuyOptionCard {...cardProps} />
                    )}
                </div>
            </VStack>
        );
    }

    return (
        <VStack spacing="md">
            <VStack spacing={isMobileViewport || (isTabletViewport && isModal) ? "md" : "xl"}>
                <HStack justify="space-between">
                    <div data-testid="product-header">
                        <ProductHeader
                            variant="emotional"
                            isOwnerActive={designer.active}
                            isOwnerFeedLinkable={designer.active}
                            ownerName={designer.name}
                            designerUrl={designer.url}
                            shortDescription={product.short_description}
                            isModal={isModal}
                        />
                    </div>
                    {canUseMembership() && !isModal && (
                        <SaveForLaterButton
                            isLoggedIn={isLoggedIn}
                            type="icon"
                            variant="emotional"
                            productId={product.product_id}
                            productStatus={buyOptionStatus}
                            pageSource="pdp"
                            className={styles.saveForLaterBtn}
                            linkId={buyOption.link_id}
                        />
                    )}
                </HStack>

                <VStack spacing={isMobileViewport || (isTabletViewport && isModal) ? "sm" : "md"}>
                    <HStack justify={"space-between"}>
                        <Price
                            {...buyOptionPrice}
                            size={isDesktopViewport ? "lg" : isModal ? "xm" : "lg"}
                        />
                        {stickerType && <Sticker name={stickerType} />}
                    </HStack>
                    {isExpressCheckout &&
                        (isChatGptExpressCheckout ? (
                            <ExpressCheckoutDisclaimer
                                productId={product.product_id}
                                buyOption={activeBuyOption}
                            />
                        ) : (
                            <VStack spacing="sm">
                                <ExpressCheckoutBuyDirectButton buyOption={activeBuyOption} />
                                <ExpressCheckoutOrSeparator />
                                <ExpressCheckoutDisclaimer
                                    productId={product.product_id}
                                    buyOption={activeBuyOption}
                                />
                            </VStack>
                        ))}
                    {sizes && (
                        <SizeArea
                            sizes={sizes}
                            isSizePickerExpanded={isSizePickerExpanded}
                            handleSizeLinkClick={handleSizeLinkClick}
                            isModal={!!isModal}
                        />
                    )}
                    <div data-testid="buy-button-product-info" ref={buyOnStoreBtnRef}>
                        <BuyButton
                            buyOption={buyOption}
                            {...(buyOption.is_icon
                                ? {
                                      isCheckout: true,
                                      buyOptionPosition: activeBuyOptionIndex,
                                      sendExtraAnalytics: !isExpressCheckout,
                                  }
                                : { isCheckout: false })}
                        />
                    </div>
                    {isChatGptExpressCheckout && (
                        <VStack spacing="sm" dataTestId="chatgpt-express-checkout-affiliate-option">
                            <ExpressCheckoutOrSeparator />
                            <ExpressCheckoutBuyDirectButton buyOption={activeBuyOption} />
                        </VStack>
                    )}
                </VStack>
            </VStack>
            <VStack spacing="sm">
                <PricingCardPDP
                    retailerName={buyOption.retailer_name}
                    sizeList={cardSizeList}
                    shippingInformation={shippingInformation}
                    leadUrl={buyOption.affiliate_url}
                    isSizeSelected={selectedSizeOption !== null}
                    promotion={promotion}
                />
            </VStack>
            {!isExpressCheckout && buyOptions.length > 1 && (
                <ComparePriceLink count={displayedBuyOptions.length} onClick={onClickCompare} />
            )}
        </VStack>
    );
}
